import {
  CreateParams,
  CreateResult,
  DeleteManyParams,
  DeleteManyResult,
  DeleteParams,
  DeleteResult,
  GetListParams,
  GetListResult,
  GetManyParams,
  GetManyReferenceParams,
  GetManyReferenceResult,
  GetManyResult,
  GetOneParams,
  GetOneResult,
  HttpError,
  UpdateManyParams,
  UpdateManyResult,
  UpdateParams,
  UpdateResult } from 'ra-core'
import { AxiosError, AxiosResponse } from 'axios';
import retrieveAxiosErr from '@/utils/retrieveAxiosErr';
import { AnyAction } from 'redux';


export interface Adapter {
  getList: (
      params: GetListParams
  ) => Promise<GetListResult>;

  getOne: (
      params: GetOneParams
  ) => Promise<GetOneResult>;

  getMany: (
      params: GetManyParams
  ) => Promise<GetManyResult>;

  getManyReference: (
      params: GetManyReferenceParams
  ) => Promise<GetManyReferenceResult>;

  update: (
      params: UpdateParams
  ) => Promise<UpdateResult>;

  updateMany: (
      params: UpdateManyParams
  ) => Promise<UpdateManyResult>;

  create: (
      params: CreateParams
  ) => Promise<CreateResult>;

  delete: (
      params: DeleteParams
  ) => Promise<DeleteResult>;

  deleteMany: (
      params: DeleteManyParams
  ) => Promise<DeleteManyResult>;

}

export type DataProviderMethods =
  'create' | 'delete' | 'deleteMany' | 'getList' | 'getMany' | 'getManyReference' | 'getOne' | 'update' | 'updateMany'

export type SupportedResources =
  // resources that registered as routes, they are long-living resources:
  'profile' | 
  'settings/organization/profile' | 'settings/organization/user-manage' |
  'settings/organization/ip-whitelist' |
  'settings/organization/sso-config' | 'settings/organization/cloud-subscription' |
  'settings/notification' | 'settings/deployment/network-configuration' |
  'settings/governance/data-classification' | 'settings/governance/data-access-role' | 'settings/governance/data-owner-group' |
  'settings/encryption/keys' | 'settings/encryption/tokens' | 'settings/encryption/obfuscation-rule' |
  'backups-n-validations/dashboard' | 'backups-n-validations/list' |
  'encryption/dashboard' | 'encryption/list' | 'encryption/custom-rules' | 'encryption/mappings' |
  'encryption/mappings/:dbId/clients' | 'encryption/file-mappings/:tmplId/clients' |
  'encryption/obfs/mappings' | 'encryption/obfs/list' | 'encryption/subsetting/mastertables' |
  'deployments/dashboard' | 'deployments/list' |
  'governance/dashboard' | 'governance/data-catalog' | 'governance/data-ownership' |
  'governance/database-semantic' | 'governance/schema-semantic' | 'governance/table-semantic' | 'governance/database-semantic/:dbId/table-fields' | 'governance/table-semantic-statis' |
  'performance-tuning/dashboard' | 'secure-files/dashboard'| 'secure-files/file-catalog'| 'secure-files/file-access-log'|
  'secure-files/offline-files' | 'settings/securefile/classification-rules' | 'settings/securefile/data-protection-rules' |
  'settings/securefile/info-types' | 'settings/securefile/document-types' | 'settings/securefile/internal-data-analyzer' |
  'settings/encryption/info-types' | 'settings/encryption/classification-rules' | 'settings/encryption/data-protection-rules' |
  'settings/secureai/images' | 'settings/secureai/mcp-servers' | 'settings/secureai/info-types' |
  'secure-files/file/data-owner-group' | 'secure-files/plainfile/data-owner-group' |


  // scoped resources, registered by useResourceRegister() hook, they will be disposed when component scope is unmounted:
  /// 1. user manage
  'user-invitation' | 'org-roles' | 'org-roles-permits' | 'org-roles-default' |
  'user-invitation-validate' | 'noop' |
  'timezone-options' | 'user-register-activate' | 'users-browse' | 'users-list-by-dar' |
  
  /// 2. login/register etc.
  'login' | 'resetpassword' | 'resetpassword-validate' | 'resetpassword-confirm' | 'changepassword' | 'phone-veri-code' |
  'sso-redirect' | 'device-verify' | 'device-consent' | 'login-preload' | 'sso-logout' | 
  'resetpassword-issue-otp' | 'restpassword-otp' |

  /// 2.1 settings
  'settings-encrypt-key-assign' | 'settings-data-owner-group-assignment-brief' |
  'settings-select-databases' | 'settings-select-tables' | 'settings-select-fields' |
  'settings-obfs-rule-usage-brief' | 'settings-cloud-subscription-test' | 'settings-cloud-subscription-consent-url' |
  'settings-client-force-refresh-key' | 'settings-client-share-secret' | 'settings-obfs-rule-upload' |
  'settings-saml-slo-cert' | 'settings-clients-driver-encrypttool' | 'settings-clients-securefile' |
  'settings-clients-securefile-register-code' | 'settings-clients-securefile-config' | 'settings-clients-securefile-autodiscovery' |
  'settings-clients-securefile-customkey-config' | 'settings-clients-securefile-excessive-decrypt-config' |
  'settings-clients-securefile-serveredition' |
  'settings-securefile-per-client' | 'settings-clients-securefile-legal-warning-config' |
  'settings-securefile-per-client/rescan' |
  'settings-secureai-app-register-code' | 'settings-secureai-mcpservers-register-code' |

  /// 2.2 products release
  'release/encryptiontool' | 'release/securefile' | 'release/jdbc' | 'release/odbc' | 'release/sqlclient' | 'download-product-release' | 'download-product-release2' |

  /// 3. backup
  'databases' | 'database-types' | 'database-engine-versions' | 'backup-recs' | 'backup-retention-days' |
  'upload-config-file' | 'download-config-file' | 'validation-schedule' |
  'databases/:id/keys-policy' | 'database-policy-override' | 'databases/:id/encryption-setting-change' |
  'database-clone' | 'database-notification-alert' |
  
  'dbmetrics-dbsize' | 'dbmetrics-indexsize' | 'dbmetrics-rowcount' | 'backup-schedule' | 'backup-schedule-type' |
  
  /// 4. encrypt
  'tables' | 'pending-tables' | 'fields' | 'mapping-rules' | 'encryption-historic-recs' |
  'collections' | 'pending-collections' | 'collection-properties' | 'encryption/mappings/nosql' |
  'encryptable-backups' | 'mappings-update' | 'encryption-rec-file' | 'db-fields-mapping-count' |
  'obfs-table' | 'obfs-field' | 'obfuscation-historic-recs' | 'token-validation-key-regenerate' |
  'token-validation-keys' | 'encrypted-dbs' | 'db-esc-status' | 'table-rename-hint' |
  'file-encrypt-template' | 'file-encrypt-template-download' | 'file-encrypt-rule-map' | 'subset-tablerefs' | 'subset-database-default-limit' |
  'copy-table-mappings' | 'copy-file-mappings' | 'client-access-policy' | 
  
  /// 5. deploy
  'deployments' | 'network-connection-sku' | 'network-connections' | 'host-options' | 'random-key' |
  'deployment-start' | 'deployment-stop' |
  'dbmetrics-maxquerytime' | 'dbmetrics-averagequerytime' | 'dbmetrics-queryerrors' |
  'dbmetrics-vmcpu' | 'dbmetrics-vmmemory' | 'dbmetrics-vmdisk' |
  
  /// 6. governance
  'governance' | 'databases-dictionary' |
  'govrnc-databases' | 'govrnc-schemas' | 'govrnc-tables' | 'govrnc-fields' |
  'govrnc-tags' | 'govrnc-catalog-classi-statics' | 'govrnc-catalog-encrypt-statics' |
  'govrnc-retention-rule' | 'govrnc-reason-collect' | 'govrnc-select-all-fields' |
  'govrnc-encrypt-rule-mapping' | 'govrnc-data-catalog-bulk' | 'govrnc-vali-bulk' |
  'govrnc-data-ownership' | 'govrnc-dashboard' | 'govrnc-catalog-export' |
  'settings/governance/classification/data-access-role/grants' |

  /// 7. document
  'downloadScripts' |

  /// 8. performance
  'dbmetrics-querywaittime' | 'dbmetrics-timeoutnum' |

  /// 9. securefiles
  'secure-files' | 'secure-files-dashboard' | 'secure-files-instances' | 'secure-files-instances-count' | 'secure-files-tags' |
  'secure-files-base-info-type' | 'secure-files-doc-tmpl-upload' | 'secure-files-doc-tmpl-download' | 'secure-files-check-onpremise-analyzer' |
  'data-analyzer-register-codes' | 'secure-files-clss-rule-set-confirm' | 'secure-files-clss-rule-trial' | 'secure-files-clss-rule-trial-statis' |
  'secure-files-clss-rule-revert' | 'data-analyzer-elect' | 'securefile-dm-reports-statis-charts' | 'securefile-dm-reports-statis-table' | 'securefile-dm-client-names' |
  'secure-files/pfs' | 'secure-files-pfs-instances' | 'secure-files-catalog-export' | 'secure-files-catalog-export-with-instance' | 'data-analyzer-image-pull-pass' |
  'secure-files/pfs-statis' | 'secure-files/file-catalog-statis' | 'secure-files-ownership-rules' | 'secure-files-ownership-statis-1' | 'secure-files-ownership-statis-2' |
  'secure-files/dm-report-file-refs-r' |
  'secure-files/dm-report-file-refs-0' |
  'secure-files/dm-report-file-refs-1' |
  'secure-files/audit/user-options' | 'secure-files/access/user-options' | 'secure-files/machine-options' |
  'secure-files/clients/:clientId/dm-scan-info' | 'secure-files/clients/serveredition' |
  
  /// 10. secure-ai
  'secure-ai' | 'secure-ai/dashboard' | 'secure-ai/app' | 'secure-ai/mcp-servers' | 'secure-ai/mcp-servers/search' |
  'secure-ai-error-logs'


export type AdapterTable = Record<SupportedResources, Partial<Adapter>>


export function getOneResp<T, T1 = T>(...transforms: Array<(x:T)=>T>) {
  //@ts-ignore
  return (resp: AxiosResponse<T>): GetOneResult<T1> => {
    const data = resp.data
    const newData = transforms.reduce((acc, fn) => fn(acc), data)
    return { data: (newData as any) }
  }
}

export function idAlias<T, P extends keyof T>(alias: P) {
  return (data: T): T & RecPatch => ({ ...data, id: data[alias] })
}

export function substitute<T, P extends keyof T>(key: P, value: any) {
  return (data: T): T => ({ ...data, [key]: value, [`_prev_${key}`]: data[key] })
}

export function feedEmptyResp(): CreateResult {
  //@ts-ignore
  return { data: {id: -1} }
}

export function feedEmptyDeleteResp(id?: any): DeleteResult {
  return { data: {id: id || -1} }
}

export function catchErr(err: any) {
  if (err?.isAxiosError) {
    const axiosErr = err as AxiosError
    const errMsg = retrieveAxiosErr(axiosErr)
    return Promise.reject(new HttpError(errMsg, axiosErr.code, axiosErr.response))
  }

  const errMsg = err.message || err || 'unknown error occurred'
  return Promise.reject(new HttpError(errMsg, 400))
}

export function tap<T extends Function>(fn: T): T {
  //@ts-ignore
  return function wrapper(...inputs: any[]) {
    console.log('Data adapter tapped:')
    console.log(inputs)
    return fn(...inputs)
  }
}

export interface RecPatch {
  id: any
}

export function wrapFileDownEffect(defaultFilename: string, forceUTF8BOM = false) {
  return async (resp: AxiosResponse<any>) => {
    const filename = ((resp.headers['content-disposition'] || defaultFilename) as string).replace(/(.*filename=)(.*?)((;.*)|$)/, (...ms) => ms[2])
    let data = resp.data
    if (forceUTF8BOM) {
      const bom = new Uint8Array([0xEF, 0xBB, 0xBF]);
      if (data instanceof ArrayBuffer) {
        data = new Uint8Array([...bom, ...new Uint8Array(data)]);
      } 
      else if (typeof data === 'string') {
        const encoder = new TextEncoder();
        data = new Uint8Array([...bom, ...encoder.encode(data)]);
      }
      else {
        console.warn('无法确定数据类型，尝试转换为 ArrayBuffer');
        data = new Uint8Array([...bom, ...new Uint8Array(await data.arrayBuffer())]);
      }
    }
    const blob = new Blob([data], { type: resp.headers['content-type'] || 'text/csv;charset=utf-8' });

    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', filename)
    document.body.appendChild(link)
    link.click()

    setTimeout(() => {
      if (url) {
        try
        {
          window.URL.revokeObjectURL(url)
        } catch {}
      }
      link && link.remove()
    }, 5000)
  }
}

export function injectSimulatedGetOne(resource: string, payload: GetOneResult): AnyAction {
  return ({
    type: 'OD/SIMULATED_SUCCESS_REQUEST',
    payload,
    meta: {
      resource,
      fetchResponse: 'GET_ONE',
      fetchStatus: 'RA/FETCH_END',
    }
  })
}

export function injectSimulatedGetList(resource: string, payload: GetListResult): AnyAction {
  return ({
    type: 'OD/SIMULATED_SUCCESS_REQUEST',
    payload,
    meta: {
      resource,
      fetchResponse: 'GET_LIST',
      fetchStatus: 'RA/FETCH_END',
    }
  })
}

export function injectSimulatedGetMany(resource: string, payload: GetManyResult): AnyAction {
  return ({
    type: 'OD/SIMULATED_SUCCESS_REQUEST',
    payload,
    meta: {
      resource,
      fetchResponse: 'GET_MANY',
      fetchStatus: 'RA/FETCH_END',
    }
  })
}

export function injectSimulatedOptimisticDelete(resource: string, payload: DeleteResult): AnyAction {
  return ({
    type: 'OD/SIMULATED_SUCCESS_REQUEST',
    payload:payload.data,
    meta: {
      resource,
      optimistic: true,
      fetch: 'DELETE',
    }
  })
}


